import { useCallback, useEffect, useMemo, useState } from "react"
import { useAuth0AccessToken } from "@/hooks/useAuth0AccessToken"
import type { ContactAttachment } from "@/types/vacancy"

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL!
const SUPABASE_ANON = import.meta.env.VITE_SUPABASE_ANON_KEY!
const EDGE_PATH = "/functions/v1/get-contact-attachments"

type ContactAttachmentsResponse = {
  contactId: string
  attachments: ContactAttachment[]
  lastUpdated?: string | null
}

export function useContactAttachments(contactId: string | null | undefined) {
  const [data, setData] = useState<ContactAttachmentsResponse | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<unknown>(null)
  const getAccessToken = useAuth0AccessToken()

  const endpoint = useMemo(() => {
    if (!contactId) {
      return null
    }
    const qs = new URLSearchParams({ contact: contactId })
    return `${SUPABASE_URL}${EDGE_PATH}?${qs.toString()}`
  }, [contactId])

  const buildHeaders = useCallback(async () => {
    const headers: Record<string, string> = { "Content-Type": "application/json" }
    if (SUPABASE_ANON) {
      headers.apikey = SUPABASE_ANON
      headers.Authorization = `Bearer ${SUPABASE_ANON}`
    }
    headers["x-auth-token"] = `Bearer ${await getAccessToken()}`
    return headers
  }, [getAccessToken])

  const fetchNow = useCallback(async () => {
    if (!endpoint) {
      setData(null)
      return
    }

    setLoading(true)
    setError(null)

    try {
      const headers = await buildHeaders()
      const response = await fetch(endpoint, {
        method: "GET",
        headers,
        credentials: "omit",
        cache: "no-store",
      })

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`)
      }

      const payload = (await response.json()) as ContactAttachmentsResponse
      setData(payload)
    } catch (err) {
      setError(err)
    } finally {
      setLoading(false)
    }
  }, [buildHeaders, endpoint])

  useEffect(() => {
    void fetchNow()
  }, [fetchNow])

  return { data, loading, error, refetch: fetchNow }
}
